import { morphSection } from '@theme/section-renderer';

const POLL_MS = 60000;
const REQUEST_TIMEOUT_MS = 10000;

function isCurrent(data, now) {
  if (data.state === 'manual' || data.state === 'evergreen') return true;
  const start = Number(data.startsAt) * 1000;
  const end = Number(data.expiresAt) * 1000;
  return data.state === 'campaign' && Number.isFinite(start) && Number.isFinite(end)
    && start > 0 && end > start && now >= start && now < end;
}

class ArtestaHeroLifecycle extends HTMLElement {
  connectedCallback() {
    if (this.listening) return;
    this.listening = true;
    this.onResume = () => this.tick(true);
    document.addEventListener('visibilitychange', this.onResume);
    window.addEventListener('pageshow', this.onResume);
    window.addEventListener('online', this.onResume);
    this.tick(false);
  }

  disconnectedCallback() {
    this.listening = false;
    clearTimeout(this.timer);
    clearTimeout(this.requestTimer);
    this.request?.abort();
    document.removeEventListener('visibilitychange', this.onResume);
    window.removeEventListener('pageshow', this.onResume);
    window.removeEventListener('online', this.onResume);
  }

  tick(refresh) {
    clearTimeout(this.timer);
    if (!this.isConnected) return;
    const current = isCurrent(this.dataset, Date.now());
    const section = this.closest('.shopify-section');
    if (!section) return;
    // Expiry is local and never waits for Shopify or a slow request.
    if (!current) section.setAttribute('data-artesta-hero-expired', '');
    if (document.hidden) return;
    const boundaries = [this.dataset.expiresAt, this.dataset.nextStart]
      .map(value => Number(value) * 1000 - Date.now()).filter(value => value > 0);
    this.timer = setTimeout(() => this.tick(true), Math.min(POLL_MS, ...boundaries));
    if (refresh || !current) void this.refresh(section);
  }

  async refresh(section) {
    if (this.request || window.Shopify?.designMode) return;
    const controller = new AbortController();
    this.request = controller;
    this.requestTimer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      // Keep the current domain, locale path and preview context, never redirect to ES.
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('_artesta_hero_refresh', String(Date.now()));
      const response = await fetch(url, {signal: controller.signal, cache: 'no-store'});
      if (!response.ok) throw new Error('Hero refresh failed');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.getElementById(section.id);
      const marker = next?.querySelector('artesta-hero-lifecycle');
      if (!marker || marker.dataset.sectionId !== this.dataset.sectionId
          || marker.dataset.locale !== this.dataset.locale
          || marker.dataset.country !== this.dataset.country
          || !isCurrent(marker.dataset, Date.now())) throw new Error('Invalid hero response');
      if (!this.isConnected || controller.signal.aborted) return;
      // One native theme morph updates image, copy, colors and CTAs together.
      await morphSection(this.dataset.sectionId, html);
      // Horizon preserves runtime data-* attributes, including the expiry guard.
      const updated = document.getElementById(section.id);
      const updatedMarker = updated?.querySelector('artesta-hero-lifecycle');
      if (updatedMarker && isCurrent(updatedMarker.dataset, Date.now())) {
        updated.removeAttribute('data-artesta-hero-expired');
      }
      if (this.isConnected) this.tick(false);
    } catch {
      // Retain a still-valid hero, but never restore an expired offer on errors.
      if (this.isConnected && !isCurrent(this.dataset, Date.now())) section.setAttribute('data-artesta-hero-expired', '');
    } finally {
      clearTimeout(this.requestTimer);
      if (this.request === controller) this.request = null;
    }
  }
}

if (!customElements.get('artesta-hero-lifecycle')) {
  customElements.define('artesta-hero-lifecycle', ArtestaHeroLifecycle);
}
